/**
 * @fileoverview FollowUpFields Component - Compact Design
 * @module FollowUpFields
 * 
 * Renders conditional follow-up fields when checklist items are selected.
 * Redesigned for minimal footprint with inline field layout.
 */

import React from 'react';
import { ChevronRight } from 'lucide-react';

export interface FollowUpField {
  name: string;
  label: string;
  type: 'number' | 'text' | 'select';
  options?: string[] | { value: string; label: string }[];
  placeholder?: string;
  suffix?: string;
  prefix?: string;
}

export interface FollowUpConfig {
  key: string;
  fields: FollowUpField[];
  instruction?: string;
}

export interface FollowUpFieldsProps {
  config: FollowUpConfig;
  values: Record<string, string | number>;
  onChange: (fieldName: string, value: string | number) => void;
  isVisible: boolean;
}

const FollowUpFields: React.FC<FollowUpFieldsProps> = ({
  config,
  values,
  onChange,
  isVisible
}) => {
  if (!isVisible) return null;

  return (
    <div 
      className="mt-3 ml-6 mb-2 pl-3 border-l-2 border-orange-200 animate-in fade-in slide-in-from-top-1 duration-150"
      role="region"
      aria-label="Additional details"
    >
      {config.instruction && (
        <p className="text-xs text-orange-700 mb-2 flex items-center gap-1">
          <ChevronRight className="w-3 h-3" />
          {config.instruction}
        </p>
      )}
      
      <div className="flex flex-wrap gap-2">
        {config.fields.map((field) => (
          <div key={field.name} className="flex-1 min-w-[120px] max-w-[200px]">
            <label 
              htmlFor={`followup-${config.key}-${field.name}`}
              className="block text-[10px] uppercase tracking-wide font-medium text-gray-500 mb-0.5"
            >
              {field.label}
            </label>
            
            {field.type === 'select' ? (
              <select
                id={`followup-${config.key}-${field.name}`}
                value={values[field.name] || ''}
                onChange={(e) => onChange(field.name, e.target.value)}
                className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded text-sm focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="">{field.placeholder || 'Select'}</option>
                {field.options?.map((opt) => {
                  const isStringOption = typeof opt === 'string';
                  const value = isStringOption ? opt : opt.value;
                  const label = isStringOption ? opt : opt.label;
                  return (
                    <option key={value} value={value}>{label}</option>
                  );
                })}
              </select>
            ) : (
              <div className="relative flex items-center">
                {field.prefix && (
                  <span className="absolute left-2 text-gray-400 text-xs">{field.prefix}</span>
                )}
                <input
                  id={`followup-${config.key}-${field.name}`}
                  type={field.type}
                  value={values[field.name] || ''}
                  onChange={(e) => onChange(field.name, field.type === 'number' ? e.target.value : e.target.value)}
                  placeholder={field.placeholder}
                  className={`w-full py-1.5 bg-white border border-gray-200 rounded text-sm focus:ring-1 focus:ring-orange-500 focus:border-orange-500 ${
                    field.prefix ? 'pl-5 pr-2' : 'px-2'
                  } ${field.suffix ? 'pr-12' : ''} ${
                    field.type === 'number' && field.suffix 
                      ? '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none' 
                      : ''
                  }`}
                />
                {field.suffix && (
                  <span className="absolute right-2 text-gray-400 text-xs">{field.suffix}</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FollowUpFields;
